import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Ticket, Trophy } from "lucide-react";

export default function RaffleCard({ onBalanceChanged }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  const load = useCallback(() => {
    api.get("/raffle/current").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load, user]);

  const buy = async () => {
    if (!user) { nav("/login"); return; }
    if (qty < 1 || qty > 100) {
      toast.error("Buy 1–100 tickets at a time.");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/raffle/buy", { quantity: qty });
      toast.success(`Purchased ${data.tickets_purchased} ticket${data.tickets_purchased === 1 ? "" : "s"}!`);
      load();
      onBalanceChanged?.(data.new_balance);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBuying(false);
    }
  };

  if (!info) return null;

  const totalCost = qty * (info.ticket_cost_credits || 100);

  return (
    <section className="border-t border-white/5 py-12 bg-ink-900" data-testid="raffle-card-section">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
        <div className="pfc-card p-8 lg:p-10 border-pfc-gold/40" data-testid="raffle-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="text-pfc-gold" size={18} />
                <span className="label text-pfc-gold">Monthly Raffle · {info.month}</span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl text-white uppercase leading-[0.95]">
                Win the <span className="gold-text">Front Cover.</span>
              </h3>
              <p className="text-white/65 font-body mt-3 max-w-xl">
                Buy raffle tickets with PFC Credits. One name pulled every month — winner gets <strong className="text-white">{info.prize_credits} credits</strong>,
                the magazine <strong className="text-white">front cover</strong>, the <strong className="text-white">highest promo for the month</strong>,
                and auto-shipped <strong className="text-white">PFC hat + t-shirt</strong>.
              </p>
              <p className="text-white/45 font-body text-xs mt-3">
                Tickets: {info.total_tickets_sold} sold this month · Your tickets: {info.my_tickets} · Draw: {info.draw_date}
              </p>
            </div>
            <div className="ml-auto flex flex-col items-end gap-2">
              <div className="font-display text-5xl gold-text leading-none">{info.ticket_cost_credits}</div>
              <div className="label text-pfc-gold">credits per ticket</div>
            </div>
          </div>

          <div className="mt-7 flex items-center gap-3 flex-wrap" data-testid="raffle-buy-row">
            <label className="label text-white/70">Quantity (1-100)</label>
            <input
              type="number" min={1} max={100}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-24 bg-ink-900 border border-white/15 text-white px-3 py-2 font-body focus:border-pfc-gold focus:outline-none"
              data-testid="raffle-qty"
            />
            <span className="font-display text-2xl text-white/80">= {totalCost} credits</span>
            <button
              onClick={buy}
              disabled={buying}
              className="ml-auto font-sub uppercase tracking-[0.25em] text-xs px-6 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50 flex items-center gap-2"
              data-testid="raffle-buy-btn"
            >
              <Ticket size={14} /> {buying ? "Buying…" : `Buy ${qty} Ticket${qty === 1 ? "" : "s"}`}
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-white/10">
            <Link
              to="/giveaway"
              className="font-sub uppercase tracking-[0.25em] text-xs text-pfc-gold hover:text-pfc-gold-bright transition"
              data-testid="raffle-see-winners"
            >
              View Past Winners + Giveaway Page →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
